import { Injectable, inject } from "@angular/core";
import { ResourceAvailability } from "@app/models/rest/resource-availability";
import {
  EventModel,
  ProjectModel,
  ResourceTimeRangeModel
} from "@bryntum/scheduler-thin";
import { AvailabilityType } from "@app/models/enums/availability-type.enum";
import { Ticket, Ticket_DetailProjection } from "@app/models/rest/ticket";
import { TicketCompactRenderer } from "@app/view/planning/schedule-viewer/ticket-compact-renderer";
import moment from "moment";
import { TicketStatus } from "@app/models/enums/ticket-status.enum";
import { SolverTicketDto } from "@app/models/dto/solver-ticket-dto";
import { NewSolutionEvent } from "@app/models/dto/new-solution-event";
import { SolverInfo, SolverIssue } from "@app/models/dto/solver-issue";
import { CustomResourceModel } from "@app/models/bryntum-ext/custom-resource-model";
import { Team } from "@app/models/rest/team";
import { Priority } from "@app/models/rest/priority";
import { TicketCompletionCode } from "@app/models/rest/ticket-completion-code";
import { SolverService } from "@app/services/rest/solver.service";
import { ResourceStore } from "@app/services/store/resource-store";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import { TicketCompletionCodeStore } from "@app/services/store/ticket-completion-code-store";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { UserService } from "@app/services/rest/user.service";
import { PriorityStore } from "@app/services/store/priority-store";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { BehaviorSubject } from "rxjs";
import { ResourceCollection } from "@lagoshny/ngx-hateoas-client";
import { ResourceModel, WorkMode } from "@app/models/rest/resource";
import { OperativeLogService } from "@app/integrations/urm/services/operative-log.service";
import {
  ActivityLogRule,
  ClientConfigService
} from "@app/core/config/client-config.service";
import { VisitType } from "@app/models/rest/visit-type";
import { VisitTypeStore } from "@app/services/store/visit-type-store";
import {
  buildSkillText,
  buildZoneText,
  getEffectiveSkills,
  getEffectiveZones
} from "@app/view/planning/schedule-viewer/effective-resource-facts";

@Injectable({
  providedIn: "root"
})
export class ScheduleProjectService {
  protected solverService = inject(SolverService);
  protected resourceStore = inject(ResourceStore);
  protected resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  protected ticketCompletionCodeStore = inject(TicketCompletionCodeStore);
  protected ticketStore = inject(TicketStreamingStore);
  protected userService = inject(UserService);
  protected serviceErrorHandler = inject(ServiceErrorHandlerService);
  protected operativeLogService = inject(OperativeLogService);
  protected priorityService = inject(PriorityStore);
  protected config = inject(ClientConfigService);
  protected visitTypeStore = inject(VisitTypeStore);

  public projectData: BehaviorSubject<ProjectModel> =
    new BehaviorSubject<ProjectModel>(new ProjectModel({}));
  public initStartDate: Date = null;
  public initEndDate: Date = null;
  public totalTickets = 0;
  public totalLoaded = 0;
  public baseDataLoaded = false;
  public resourcesLoaded = false;
  public availabilityLoaded = false;
  public ticketLoaded = false;
  public initialized: boolean = false;
  public updatedTicketCount = 0;
  private lastStartDate: Date = null;
  private lastEndDate: Date = null;
  private error: boolean;
  private teamList: Team[];
  private dataLoading: boolean = true;
  private priorityMap: Map<number, Priority> = new Map<number, Priority>();
  private completionCodeMap: Map<number, TicketCompletionCode> = new Map<
    number,
    TicketCompletionCode
  >();
  private previousIssues: Map<string, SolverIssue> = new Map<
    string,
    SolverIssue
  >();
  private plannerId: number;
  private visitTypeMap: Map<number, VisitType> = new Map<number, VisitType>();

  public async loadProject() {
    this.loadBaseData().then(() => {
      this.subscribeToEntities();
      this.solverService.status().then();
      this.loadResources();
      console.log("loading ticket state");
      this.solverService.ticketState().then();

      this.solverService.updateStream.subscribe((value: SolverInfo) => {
        // console.log('new solution' + value);
        this.newSolutionChange(value.changeEvent);
        if (value.issues) {
          this.issueListChange(value.issues);
        }
      });

      if (this.config.config.urmServices?.activityLog?.enabled) {
        setInterval(() => {
          this.refreshActivity();
        }, this.config.config.urmServices.activityLog.refreshIntervalSeconds * 1000);
      }
      this.error = false;
    });
  }

  public loadBaseData(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let plannerPromise = this.userService.getPlanner();
      plannerPromise
        .then((planner) => {
          this.teamList = planner.teamList;
          this.plannerId = planner.id;
        })
        .catch((reason) => reject(reason));
      let priorityPromise = this.priorityService.loadAll();
      priorityPromise
        .then((priorityList) => {
          priorityList.resources.forEach((value) => {
            this.priorityMap.set(value.id, value);
          });
        })
        .catch((reason) => reject(reason));
      let completionCodePromise = this.ticketCompletionCodeStore.loadAll();
      completionCodePromise
        .then((completionCodeList) => {
          completionCodeList.resources.forEach((value) => {
            this.completionCodeMap.set(value.id, value);
          });
        })
        .catch((reason) => reject(reason));
      let visitTypePromise = this.visitTypeStore.loadAll();
      visitTypePromise
        .then((visitTypeList) => {
          visitTypeList.resources.forEach((value) => {
            this.visitTypeMap.set(value.id, value);
          });
        })
        .catch((reason) => reject(reason));
      Promise.all([
        plannerPromise,
        priorityPromise,
        completionCodePromise,
        visitTypePromise
      ])
        .then(() => {
          this.baseDataLoaded = true;
          resolve(true);
        })
        .catch((reason) => reject(reason));
    });
  }

  handleResourceLoad(resources: ResourceCollection<ResourceModel>) {
    console.log("schedule viewer has received a resources subscription event");

    this.projectData.getValue().resourceStore.onBeforeFilter = (record) => {
      console.log("On before filter", record);
      //if any filter values are [] then replace with null
      (record.filters as any).forEach((filter: any) => {
        console.log("Checking filter", filter);
        // if (
        //   filter.value &&
        //   filter.value instanceof Array &&
        //   filter.value.length === 0
        // ) {
        //   filter.value = null;
        // }
      });
    };
    this.projectData.getValue().resourceStore.onFilter = (event) => {
      console.log("filtered", event);
    };
    resources.resources.forEach((resource) => {
      console.log("loading resource", resource);
      let current = this.projectData
        .getValue()
        .resourceStore.getById(resource.id);
      if (!current) {
        current = new CustomResourceModel({
          id: resource.id,
          name: resource.name
        });
        (current as CustomResourceModel).setResourceImageUrl(resource.imageUrl);
        this.projectData.getValue().resourceStore.add(current);
        current = this.projectData
          .getValue()
          .resourceStore.getById(resource.id);
      }
      (current as CustomResourceModel).setResourceImageUrl(resource.imageUrl);
      current.set("name", resource.name);
      current.set("entity", resource);
      current.set("workMode", resource.workMode);
      if (resource.team) {
        current.set("team", resource.team.name);
      }
      this.applyEffectiveResourceFacts(current, resource);
    });
    if (this.config.config.urmServices?.activityLog?.enabled) {
      this.refreshActivity();
    }
  }

  /**
   * Sets the skill/zone display and filter fields on a scheduler resource
   * record, restricted to the assignments active for the current view range
   * (assignment start/end dates and mapping-pattern windows — #1558).
   */
  private applyEffectiveResourceFacts(record: any, resource: ResourceModel) {
    const skills = getEffectiveSkills(
      resource.skillsList,
      this.initStartDate,
      this.initEndDate
    );
    record.set("skillText", buildSkillText(skills));
    record.set(
      "effectiveSkillIds",
      skills.map((resourceSkill) => resourceSkill.skill.id)
    );

    const zones = getEffectiveZones(
      resource.zonesList,
      this.initStartDate,
      this.initEndDate
    );
    record.set("zoneText", buildZoneText(zones));
    record.set(
      "effectiveZoneIds",
      zones
        .map((zone: any) => zone.zone?.id ?? zone.zoneId)
        .filter((zoneId: any) => zoneId != null)
    );
  }

  /**
   * Recomputes the effective skills/zones for every loaded resource against
   * the current view range. Called when the schedule's date range changes.
   */
  public refreshEffectiveResourceFacts() {
    const resourceStore = this.projectData.getValue().resourceStore;
    resourceStore.allRecords.forEach((record: any) => {
      const resource = record.get("entity") as ResourceModel;
      if (resource) {
        this.applyEffectiveResourceFacts(record, resource);
      }
    });
    // Combo filters on the skills/zones columns read the effective id fields,
    // so re-evaluate any applied filters against the recomputed values.
    if (resourceStore.isFiltered) {
      resourceStore.filter();
    }
  }

  subscribeToEntities() {
    console.warn("Subscribing to entities in schedule viewer");
    this.resourceAvailabilityStore.entities.subscribe((availabilities) => {
      console.log("Loading availabilities");
      availabilities.forEach(this.loadAvailability.bind(this));
    });
    this.ticketStore.updateStream.subscribe((ticket) => {
      if (ticket !== null) {
        this.refreshTicket(ticket.id);
      }
    });

    this.resourceAvailabilityStore.deleted.subscribe((value) => {
      console.log("deleted available message on schedule", value);
      if (value) {
        this.projectData.getValue().resourceTimeRangeStore.remove(value.id);
      }
    });

    this.resourceStore.entity.subscribe((value) => {
      console.log("Resource updated", value);
      if (!value) {
        return;
      }
      let current = this.projectData.getValue().resourceStore.getById(value.id);
      if (current) {
        console.log(
          "Found resource to update, setting new name to ",
          value.name
        );
        (current as CustomResourceModel).setResourceImageUrl(value.imageUrl);
        current.set("name", value.name);
        current.set("entity", value);
        current.set("workMode", value.workMode);
        this.applyEffectiveResourceFacts(current, value);
      }
    });

    this.priorityService.entity.subscribe((priority) => {
      if (priority) {
        console.log("Priority updated", priority);
        // Update the priority map with the new priority data
        this.priorityMap.set(priority.id, priority);
        // Update all existing events that use this priority
        this.updateEventsForPriorityChange(priority);
      }
    });
  }

  loadResources() {
    this.resourceStore
      .loadWithSearch(
        "findAllByPlannerId",
        {
          params: {
            plannerId: this.plannerId,
            projection: "detail"
          },
          pageParams: {
            size: 5000
          }
        },
        true
      )
      .then((resources) => {
        console.log("resource loaded", this);
        this.handleResourceLoad(resources);
        this.resourcesLoaded = true;
        this.loadAvailabilities();
      })
      .catch((reason) => {
        this.error = true;
        console.log("resource loading error");
        console.log(reason);
      });
  }

  public clearAndLoadAvailabilities() {
    this.projectData.getValue().resourceTimeRangeStore.removeAll();
    this.loadAvailabilities();
  }

  loadAvailabilities() {
    console.log("loading availabilities for teams", this.teamList);
    if (this.teamList) {
      this.resourceAvailabilityStore
        .findByTimeRangeAndTeams(
          this.initStartDate,
          this.initEndDate,
          this.teamList
        )
        .then(() => {
          this.availabilityLoaded = true;

          this.loadSolverState().then(() => {
            this.loadTickets();
          });
        })
        .catch((error) => {
          this.error = true;
          this.serviceErrorHandler.handleError(error);
        });
    } else {
      this.userService.getPlanner().then((planner) => {
        this.teamList = planner.teamList;
        this.loadAvailabilities();
      });
    }
  }

  loadTickets() {
    if (
      moment(this.initStartDate)
        .startOf("day")
        .subtract(1)
        .isBefore(moment(), "day") &&
      moment(this.initEndDate)
        .startOf("day")
        .subtract(1)
        .isBefore(moment(), "day")
    ) {
      this.ticketStore
        .findByTimeRangeAndTeams(
          this.initStartDate,
          this.initEndDate,
          this.teamList
        )
        .then((tickets: ResourceCollection<Ticket>) => {
          if (tickets.resources.length > 0) {
            tickets.resources.forEach(this.loadTicket.bind(this));
          }
          this.dataLoading = false;
          this.ticketLoaded = true;
        })
        .catch((reason) => this.serviceErrorHandler.handleError(reason));
    }
  }

  loadAvailability(availability: ResourceAvailability): any {
    let event = this.projectData
      .getValue()
      .resourceTimeRangeStore.getById(availability.id);

    if (event) {
      event.set(
        "timeRangeColor",
        availability.availabilityCode &&
          availability.availabilityCode.availabilityType ===
            AvailabilityType.BLOCK
          ? "red"
          : "green"
      );
      event.set("entity", availability);
      event.set(
        "name",
        availability.availabilityCode
          ? availability.availabilityCode.description
          : ""
      );
      event.set("startDate", new Date(availability.start));
      event.set("endDate", new Date(availability.end));
    } else {
      event = new ResourceTimeRangeModel({
        timeRangeColor:
          availability.availabilityCode &&
          availability.availabilityCode.availabilityType ===
            AvailabilityType.BLOCK
            ? "red"
            : "green",
        name: availability.availabilityCode
          ? availability.availabilityCode.description
          : "",
        startDate: new Date(availability.start),
        endDate: new Date(availability.end),
        resourceId: availability.resource.id
      });
      event.set("id", availability.id);
      event.set("entity", availability);
      this.projectData.getValue().resourceTimeRangeStore.add([event]);
    }
  }

  /**
   * Reload a single ticket from the REST detail projection and re-apply it to
   * the scheduler event store. This is the single source of truth that powers
   * both the event icons and the right-click context menu (both read the same
   * event record fields, e.g. forceStartTime / lockResourceId).
   *
   * It is driven by the live update stream, but is also called explicitly after
   * lock changes made from the schedule. Resource-lock changes hit an
   * association endpoint that does not emit a ticket update event, so unless
   * the change happens to re-position the ticket nothing else would refresh the
   * event record and the menu/icons would go stale (see issue #905).
   */
  public refreshTicket(ticketId: number): Promise<void> {
    return this.ticketStore
      .loadSingleProjection<Ticket_DetailProjection>(
        Ticket_DetailProjection,
        ticketId,
        "detail"
      )
      .then((value) => {
        this.loadTicket(value);
      });
  }

  public loadTicket(ticket: Ticket_DetailProjection) {
    if (!ticket) {
      return;
    }
    this.updatedTicketCount++;
    let existing: any = this.projectData
      .getValue()
      .eventStore.getById(ticket.id);

    let adding = false;
    if (!existing) {
      if (ticket.resourceAvailability != null) {
        adding = true;
        existing = new EventModel({
          id: ticket.id
        });
      }
    }
    if (!ticket.resourceAvailability) {
      this.projectData.getValue().eventStore.remove([existing]);
      this.projectData.getValue().eventStore.commit().then();
      return;
    }
    existing.set("entity", ticket);
    existing.set(
      "eventColor",
      TicketCompactRenderer.getTicketColour(ticket.ticketStatus)
    );
    existing.set("resourceId", ticket.resourceAvailability.resource.id);
    existing.set(
      "enrouteTime",
      moment(ticket.enrouteTime).parseZone().startOf("minute").toDate()
    );
    existing.set(
      "arrivedTime",
      moment(ticket.arrivedTime).parseZone().startOf("minute").toDate()
    );
    existing.set(
      "startedTime",
      moment(ticket.startedTime).parseZone().startOf("minute").toDate()
    );
    existing.set(
      "completedTime",
      moment(ticket.completedTime).parseZone().startOf("minute").toDate()
    );
    existing.set("priorityId", ticket.visit.job?.priority.id);
    existing.set("minimumAttendance", ticket.visit.job?.minimumAttendance);
    existing.set("ticketStatus", ticket.ticketStatus);
    existing.set(
      "followOnRequired",
      ticket.followOnRequired && !ticket.followOnActioned
    );
    existing.set("externalJobRef", ticket.visit.job?.externalJobReference);
    existing.set("meetingId", ticket.visit.meeting?.id);

    // Set meetingDescription based on visitTypeId
    if (ticket.visit.visitType.id) {
      let visitType = this.visitTypeMap.get(ticket.visit.visitType.id);
      existing.set(
        "meetingDescription",
        visitType ? visitType.description : null
      );
    } else if (ticket.visit.meeting) {
      existing.set("meetingDescription", ticket.visit.meeting.description);
    } else {
      existing.set("meetingDescription", null);
    }

    if (ticket.forceStartTime !== null) {
      existing.set("forceStartTime", ticket.forceStartTime);
    } else {
      existing.set("forceStartTime", null);
    }

    if (ticket.lockToResource) {
      existing.set("lockResourceId", ticket.lockToResource.id);
    } else {
      existing.set("lockResourceId", null);
    }
    existing.set(
      "canDespatch",
      ticket.ticketStatus === TicketStatus.PLANNING &&
        this.canDespatch(ticket.resourceAvailability.resource.id)
    );
    if (ticket.visit.job) {
      existing.set(
        "priorityBackground",
        this.priorityMap.get(ticket.visit.job.priority.id).backgroundColor
      );
      existing.set(
        "priorityForeground",
        this.priorityMap.get(ticket.visit.job.priority.id).foregroundColor
      );
    }

    if (ticket.ticketCompletionCode) {
      let result = this.completionCodeMap.get(ticket.ticketCompletionCode.id);
      existing.set("completionText", result.description);
      existing.set("completionCode", result.code);
      existing.set("completionBackground", result.backgroundColor);
      existing.set("completionForeground", result.foregroundColor);
    }

    existing.set("name", ticket.ticketStatus.toString());
    existing.resizable = false;
    // Orchestrator/project visits must not be dragged to another resource or
    // time — all changes happen in Orchestrator (#1529). Events loaded from the
    // solver snapshot lack job data, so TicketEditDialog re-checks on drop.
    existing.draggable =
      ticket.ticketStatus === TicketStatus.PLANNING &&
      !ticket.visit.job?.externalProjectReference;

    existing.setStartDate(
      moment(ticket.enrouteTime).parseZone().startOf("minute").toDate()
    );
    existing.setEndDate(
      moment(ticket.completedTime).parseZone().startOf("minute").toDate()
    );

    if (adding) {
      console.log("committing new record", existing);
      this.projectData.getValue().eventStore.append(existing);
    }

    this.projectData.getValue().eventStore.commit().then();
  }

  prepTicketDTO(ticketDTO: SolverTicketDto) {
    let meetingDescription: string = null;
    if (ticketDTO.visitTypeId) {
      let visitType = this.visitTypeMap.get(ticketDTO.visitTypeId);
      meetingDescription = visitType ? visitType.description : null;
    }

    return {
      id: ticketDTO.ticketId,
      name: ticketDTO.ticketId,
      resourceId: ticketDTO.resourceId,
      enrouteTime: moment(ticketDTO.enrouteTime)
        .parseZone()
        .startOf("minute")
        .toDate(),
      arrivedTime: moment(ticketDTO.arrivedTime)
        .parseZone()
        .startOf("minute")
        .toDate(),
      startedTime: moment(ticketDTO.startedTime)
        .parseZone()
        .startOf("minute")
        .toDate(),
      completedTime: moment(ticketDTO.completedTime)
        .parseZone()
        .startOf("minute")
        .toDate(),
      ticketStatus: ticketDTO.ticketStatus,
      eventType: "Ticket",
      forceStartTime: ticketDTO.forceStartTime
        ? moment(ticketDTO.forceStartTime).parseZone().toDate()
        : null,
      resizable: false,
      draggable:
        ticketDTO.ticketStatus === TicketStatus.PLANNING &&
        ticketDTO.meetingId === null,
      eventColor: TicketCompactRenderer.getTicketColour(ticketDTO.ticketStatus),
      minimumAttendance: ticketDTO.minimumAttendance,
      groupedTicketId: ticketDTO.groupedTicketId,
      lockResourceId: ticketDTO.lockResourceId,
      priorityId: ticketDTO.priorityId,
      completionCodeId: ticketDTO.completionCodeId,
      followOnRequired: ticketDTO.followOnRequired,
      recallPending: ticketDTO.recallPending,
      externalJobRef: ticketDTO.externalJobRef,
      meetingId: ticketDTO.meetingId,
      meetingDescription: meetingDescription,
      canDespatch:
        ticketDTO.ticketStatus === TicketStatus.PLANNING &&
        this.canDespatch(ticketDTO.resourceId),
      completionText: ticketDTO.completionCodeId
        ? this.completionCodeMap.get(ticketDTO.completionCodeId).description
        : null,
      completionCode: ticketDTO.completionCodeId
        ? this.completionCodeMap.get(ticketDTO.completionCodeId).code
        : null,
      completionBackground: ticketDTO.completionCodeId
        ? this.completionCodeMap.get(ticketDTO.completionCodeId).backgroundColor
        : null,
      completionForeground: ticketDTO.completionCodeId
        ? this.completionCodeMap.get(ticketDTO.completionCodeId).foregroundColor
        : null,
      priorityBackground: ticketDTO.priorityId
        ? this.priorityMap.get(ticketDTO.priorityId).backgroundColor
        : "white",
      priorityForeground: ticketDTO.priorityId
        ? this.priorityMap.get(ticketDTO.priorityId).foregroundColor
        : "black",
      posX: ticketDTO.posX,
      posY: ticketDTO.posY,
      issues: null,
      startDate: moment(ticketDTO.enrouteTime)
        .parseZone()
        .startOf("minute")
        .toDate(),
      endDate: moment(ticketDTO.completedTime)
        .parseZone()
        .startOf("minute")
        .toDate(),
      nextTicketId: ticketDTO.nextTicketId
    };
  }

  async removeTicketDTO(ticketDTO: SolverTicketDto) {
    try {
      this.projectData.getValue().eventStore.remove(ticketDTO.ticketId);
    } catch (e) {}
  }

  async loadTicketDTO(ticketDTO: SolverTicketDto, replace: boolean) {
    if (
      this.projectData.getValue().eventStore.includes(ticketDTO.ticketId) &&
      !replace
    ) {
      return;
    }
    let existing: any = this.projectData
      .getValue()
      .eventStore.getById(ticketDTO.ticketId);
    let newRecord = false;
    if (!existing) {
      existing = new EventModel();
      newRecord = true;
    }
    existing.set(this.prepTicketDTO(ticketDTO));
    existing.setStartDate(
      moment(ticketDTO.enrouteTime).parseZone().startOf("minute").toDate()
    );
    existing.setEndDate(
      moment(ticketDTO.completedTime).parseZone().startOf("minute").toDate()
    );
    if (newRecord) {
      this.projectData.getValue().eventStore.add([existing]);
    }
  }

  newSolutionChange(event: NewSolutionEvent) {
    if (!event) {
      return;
    }
    console.log("updated solution " + event.updated.length);

    event.updated.forEach((value) => {
      this.loadTicketDTO(value, true);
    });
    event.added.forEach((value) => {
      this.loadTicketDTO(value, false);
    });
    event.removed.forEach((value) => {
      this.removeTicketDTO(value);
    });
    this.projectData.getValue().eventStore.commit().then();
  }

  private refreshActivity() {
    let idList = this.projectData
      .getValue()
      .resourceStore.allRecords.flatMap((value) => value.id.toString(10));
    console.log("Getting activity for IDs ", idList);
    this.operativeLogService
      .getLatestOperativeLogsByIDs(idList)
      .then((value) => {
        console.log("operative logs", value);

        value.forEach((log) => {
          console.log("Updating operative log data", log, this.config.config);
          let resource = this.projectData
            .getValue()
            .resourceStore.getById(log.EmployeeID);
          if (resource) {
            if (
              moment(log.LatestDateSystem)
                .add(
                  this.config.config.urmServices.activityLog.cutoffHours,
                  "hours"
                )
                .isAfter(moment())
            ) {
              let rule: ActivityLogRule =
                this.config.config.urmServices.activityLog.rules[log.Stage];
              resource.set("highlightRule", rule.highlightRow);
              if (rule.showStatus) {
                resource.set(
                  "activityDescription",
                  `${rule.displayAs} ${
                    rule.showTimesheetCode ? "(" + log.TimesheetCode + ")" : ""
                  } - ${moment(log.LatestDateSystem).fromNow()}`
                );
              } else {
                resource.set("activityDescription", null);
                resource.set("highlightRule", false);
              }
            } else {
              resource.set("activityDescription", null);
              resource.set("highlightRule", false);
            }
          }
        });
      })
      .catch(() => {});
  }

  private haveDatesChanged(): boolean {
    const initStart = moment(this.initStartDate);
    const initEnd = moment(this.initEndDate);
    const lastStart = moment(this.lastStartDate);
    const lastEnd = moment(this.lastEndDate);

    // Check if either start or end date has changed
    return !initStart.isSame(lastStart) || !initEnd.isSame(lastEnd);
  }

  private canDespatch(resourceId) {
    let resource = this.projectData
      .getValue()
      .resourceStore.getById(resourceId);
    if (!resource) {
      return false;
    }
    return resource.get("workMode") === WorkMode.Offline;
  }

  private loadSolverState(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.projectData.getValue().eventStore.suspendEvents();
      this.solverService
        .ticketState()
        .then((value) => {
          this.totalTickets = value.tickets.length;
          console.log("loading initial ticketState", moment().toDate());
          let prepped = [];
          value.tickets.forEach((value1) => {
            prepped.push(this.prepTicketDTO(value1));
          });

          console.log("data prepped", moment().toDate());
          this.projectData.getValue().eventStore.data = prepped;

          console.log("data set");

          this.projectData.getValue().eventStore.commit();
          this.projectData.getValue().eventStore.suspendAutoCommit();
          this.projectData.getValue().eventStore.suspendEvents();
          this.issueListChange(value.issues);
          this.projectData.getValue().eventStore.resumeAutoCommit();
          this.projectData.getValue().eventStore.resumeEvents();
          this.initialized = true;
          this.ticketLoaded = true;
          this.dataLoading = false;
          console.log("Emitting project data");
          this.projectData.next(this.projectData.getValue());
          this.projectData.getValue().eventStore.resumeEvents();
          console.log(
            `Initial ticket load complete: received ${value.tickets.length} tickets from solver, ${prepped.length} added to event store`,
            moment().toDate()
          );
          resolve(true);
        })
        .catch((reason) => this.serviceErrorHandler.handleError(reason));
    });
  }

  private areArraysEqual(a: any[], b: any[]): boolean {
    if (!a || !b) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  private issueListChange(issues: SolverIssue[]) {
    if (!issues) {
      return;
    }

    let availabilities = this.projectData
      .getValue()
      .resourceTimeRangeStore.findByField("timeRangeColor", "orange");
    availabilities.forEach((availability) => {
      availability.data.set("timeRangeColor", "green");
    });

    issues.forEach((value) => {
      if (
        value.rule !== "Normal Priority Reservation Use" &&
        value.rule !== "High Priority Reservation Use" &&
        value.rule !== "Emergency Priority Reservation Use"
      ) {
        let previous = this.previousIssues.get(value.rule);
        if (
          this.haveDatesChanged() ||
          !previous ||
          !this.areArraysEqual(value.tickets, previous.tickets) ||
          !this.areArraysEqual(
            value.resourceAvailabilities,
            previous.resourceAvailabilities
          )
        ) {
          this.updateIssueList(value, previous);
        }
      }
      this.previousIssues.set(value.rule, value);
    });

    this.projectData.getValue().eventStore.commit();
  }

  private updateIssueList(issue: SolverIssue, previous: SolverIssue) {
    console.log(
      `updating issue list, current tickets ${issue?.tickets?.length}, previous tickets: ${previous?.tickets?.length}`
    );
    let start = moment();

    if (previous && previous.tickets) {
      previous.tickets
        .filter((ticket) => !issue.tickets.includes(ticket))
        .forEach((value) => {
          let savedTicket = this.projectData
            .getValue()
            .eventStore.getById(value);
          if (!!savedTicket) {
            savedTicket.set("eventColor", "gray");
            savedTicket.set("issues", null);
          }
        });
    }
    console.log(
      "Reset previous tickets " +
        issue.rule +
        " in " +
        moment().diff(start, "milliseconds") +
        "ms"
    );
    if (issue.resourceAvailabilities) {
      issue.resourceAvailabilities.forEach((value) => {
        let savedAvailability = this.projectData
          .getValue()
          .resourceTimeRangeStore.getById(value);
        if (!!savedAvailability) {
          savedAvailability.set("timeRangeColor", "orange");
        }
      });
    }

    console.log(
      "Reset previous shifts " +
        issue.rule +
        " in " +
        moment().diff(start, "milliseconds") +
        "ms"
    );
    console.log("Date range:" + this.initStartDate + " to " + this.initEndDate);
    if (issue.tickets) {
      issue.tickets.forEach((value) => {
        let savedTicket = this.projectData.getValue().eventStore.getById(value);
        if (
          !!savedTicket &&
          moment(savedTicket.get("startDate")).isBetween(
            this.initStartDate,
            this.initEndDate
          )
        ) {
          if (savedTicket.get("eventColor") !== "red") {
            savedTicket.set("eventColor", "red");
          }
          if (!savedTicket.get("issues")) {
            savedTicket.set("issues", []);
          }
          let issues: any[] = savedTicket.get("issues");
          let matches = issues.filter(
            (savedValue) => savedValue.rule === issue.rule
          );
          if (matches.length === 0) {
            savedTicket.get("issues").push({
              rule: issue.rule,
              hard: issue.hard,
              impact: issue.impact
            });
          }
        }
      });
      console.log("updated issue list " + issue.tickets.length);
    }

    let end = moment();
    console.log(
      "Processed rule " +
        issue.rule +
        " in " +
        end.diff(start, "milliseconds") +
        "ms"
    );
  }

  private updateEventsForPriorityChange(updatedPriority: Priority) {
    console.log("Updating events for priority change", updatedPriority);
    const eventStore = this.projectData.getValue().eventStore;

    // Find all events that use this priority
    const eventsToUpdate: any[] = [];

    // Try to use the records property first if available (covers all events)
    if (eventStore.records && Array.isArray(eventStore.records)) {
      eventStore.records.forEach((event: any) => {
        if (event.get && event.get("priorityId") === updatedPriority.id) {
          eventsToUpdate.push(event);
        }
      });
    } else if (eventStore.forEachScheduledEvent) {
      // Fallback to forEachScheduledEvent if records is not available
      (eventStore as any).forEachScheduledEvent((event: any) => {
        if (event.get && event.get("priorityId") === updatedPriority.id) {
          eventsToUpdate.push(event);
        }
      });
    }

    console.log(
      `Found ${eventsToUpdate.length} events to update for priority ${updatedPriority.id}`
    );

    // Update the priority colors for each event
    eventsToUpdate.forEach((event: any) => {
      if (event.set) {
        event.set("priorityBackground", updatedPriority.backgroundColor);
        event.set("priorityForeground", updatedPriority.foregroundColor);
      }
    });

    // Commit the changes to trigger UI update
    if (eventsToUpdate.length > 0) {
      eventStore.commit();
    }
  }
}
